import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

import API, { clearAuthToken, setAuthToken } from '../Utils/api';

export const UserContext = createContext();

export const useUser = () => useContext(UserContext);

export function UserProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const setTimeoutId = useRef();
    // Bumped whenever the session ends so a refresh that was already in flight
    // can tell it is stale and must not restore the user, header or timer.
    const sessionGeneration = useRef(0);

    const setRefreshTimeout = () => {
        setTimeoutId.current = setTimeout(refreshToken, 5 * 1000 * 60);
    };

    const refreshToken = async onSuccess => {
        const generation = sessionGeneration.current;
        try {
            const res = await API.refreshToken();

            if (generation !== sessionGeneration.current) return;

            const { token: token_, ...user_ } = res.data;

            onSuccess && onSuccess(user_);

            setAuthToken(token_);
            setRefreshTimeout();
        } catch (err) {
            if (generation === sessionGeneration.current) setLoading(false);
        }
    };

    // Single teardown path for every way a session ends (sidebar logout, password
    // reset). The server logout is best effort and never awaited, so a slow or
    // failing request cannot keep the local session alive.
    const clearSession = () => {
        sessionGeneration.current += 1;
        if (setTimeoutId.current !== undefined) clearTimeout(setTimeoutId.current);
        setTimeoutId.current = undefined;

        try {
            API.logout().catch(() => {});
        } catch {
            // A failed server logout must not stop the local session from ending.
        }

        clearAuthToken();
        setUser(null);
    };

    useEffect(() => {
        refreshToken(user => {
            setUser(user);
            loading && setLoading(false);
        });

        return () => {
            sessionGeneration.current += 1;
            if (setTimeoutId.current !== undefined) clearTimeout(setTimeoutId.current);
        };

        // eslint-disable-next-line
    }, []);

    if (loading) return null;
    return <UserContext.Provider value={{ user, setUser, clearSession }}>{children}</UserContext.Provider>;
}
